import { useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fuelValueForDisplay, formatLapTime, formatFalloff, parseSetupName } from "../utils/formatters";
import type { RankingEntry } from "../types";
import WeatherBadge from "./WeatherBadge";
import HoverPopover from "./HoverPopover";

interface Props {
  rankings: RankingEntry[];
  onSort?: (field: string) => void;
}

/* ── Column definitions ── */
type SortableField = "best_lap_time" | "top10_avg" | "top20_avg" | "top30_avg" | "avg_lap_time";

interface Column {
  key: string;
  label: string;
  sortKey?: SortableField;
  dataKey?: keyof RankingEntry;
}

const COLUMNS: Column[] = [
  { key: "rank", label: "#" },
  { key: "driver", label: "Driver" },
  { key: "setup", label: "Setup" },
  { key: "best", label: "Best", sortKey: "best_lap_time", dataKey: "bestLapTime" },
  { key: "top10", label: "Top 10", sortKey: "top10_avg", dataKey: "top10Avg" },
  { key: "top20", label: "Top 20", sortKey: "top20_avg", dataKey: "top20Avg" },
  { key: "top30", label: "Top 30", sortKey: "top30_avg", dataKey: "top30Avg" },
  { key: "avg", label: "Avg", sortKey: "avg_lap_time", dataKey: "avgLapTime" },
  { key: "falloff", label: "Falloff" },
  { key: "laps", label: "Laps" },
  { key: "weather", label: "Weather" },
  { key: "fuel", label: "Fuel" },
];

const NUMERIC_FIELDS = ["bestLapTime", "top10Avg", "top20Avg", "top30Avg", "avgLapTime"] as const;

/* ── Inline styles ── */

const styles = {
  /* Container: racing stripe top border + card treatment */
  container: {
    position: "relative" as const,
    borderRadius: "var(--r-lg)",
    overflow: "hidden",
    background: "var(--ink-1)",
    border: "1px solid var(--rule)",
  },

  /* Racing stripe at top */
  racingStripe: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: "var(--live)",
    zIndex: 10,
  },

  scrollWrapper: {
    overflowX: "auto" as const,
    paddingTop: "3px", /* space for the racing stripe */
  },

  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    tableLayout: "fixed" as const,
    minWidth: "920px",
  },

  /* Normal-flow header avoids extra compositor work while the page scrolls. */
  thead: {
    position: "relative" as const,
  },

  th: (isActive: boolean, isSortable: boolean): React.CSSProperties => ({
    textAlign: "left",
    padding: "0.7rem 0.75rem",
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: isActive ? "var(--live)" : "var(--text-secondary)",
    borderBottom: "1px solid var(--rule)",
    background: "var(--ink-1)",
    cursor: isSortable ? "pointer" : "default",
    userSelect: "none",
    whiteSpace: "nowrap",
    textShadow: "none",
    position: "relative",
  }),

  /* Sort arrow */
  sortArrow: (isActive: boolean): React.CSSProperties => ({
    display: "inline-block",
    marginLeft: "4px",
    fontSize: "0.8125rem",
    color: isActive ? "var(--live)" : "transparent",
    verticalAlign: "middle",
  }),

  /* Row base — :hover background handled in CSS, click state stays in JS */
  row: (_isP1: boolean, isClicked: boolean): React.CSSProperties => ({
    cursor: "pointer",
    position: "relative",
    background: isClicked ? "var(--live-soft)" : "transparent",
    transform: isClicked ? "scale(0.998)" : "none",
    transition: "transform 0.08s ease-out",
  }),

  /* Left accent border — opacity toggled by CSS on parent :hover */
  rowAccent: (isP1: boolean): React.CSSProperties => ({
    position: "absolute" as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: "3px",
    background: isP1
      ? "linear-gradient(180deg, var(--flag-yellow) 0%, var(--live) 100%)"
      : "var(--live)",
    borderRadius: "0 2px 2px 0",
  }),

  /* Cell base */
  td: (isEvenRow: boolean, isP1: boolean): React.CSSProperties => ({
    padding: "0.6rem 0.75rem",
    borderBottom: isP1
      ? "1px solid var(--rule-strong)"
      : "1px solid var(--rule-faint)",
    verticalAlign: "middle",
    fontSize: "0.98rem",
    background: isP1
      ? "var(--live-soft)"
      : isEvenRow
        ? "var(--ink-1)"
        : "transparent",
  }),

  /* P1 row — gold/green highlight band */
  p1Glow: {
    position: "absolute" as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: "3px",
    background: "linear-gradient(180deg, var(--flag-yellow) 0%, var(--live) 100%)",
    boxShadow: "none",
    borderRadius: "0 2px 2px 0",
  },

  /* Rank badge */
  rankBadge: (rank: number, total: number): React.CSSProperties => ({
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: rank === 1 ? "1rem" : "0.9rem",
    color: rank === 1
      ? "var(--flag-yellow)"
      : rank === total
        ? "var(--flag-orange)"
        : "var(--text-secondary)",
    textShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "1.6rem",
  }),

  /* P1 rank with background pill */
  p1RankPill: {
    background: "var(--live-soft)",
    border: "1px solid var(--flag-yellow-soft)",
    borderRadius: "4px",
    padding: "2px 8px",
  },

  /* Driver name */
  driverName: (isP1: boolean): React.CSSProperties => ({
    fontFamily: "var(--font-body)",
    fontWeight: isP1 ? 600 : 500,
    color: isP1 ? "var(--text-strong)" : "var(--text-strong)",
    fontSize: "1rem",
    letterSpacing: "0.01em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),

  /* Setup version tag */
  setupTag: {
    fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
    fontSize: "0.88rem",
    color: "var(--text-secondary)",
    padding: "1px 6px",
    background: "var(--live-soft)",
    border: "1px solid var(--rule)",
    borderRadius: "3px",
    letterSpacing: "0.02em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "inline-block",
  },

  /* Lap time cell */
  lapTime: (colorVar?: string): React.CSSProperties => ({
    fontFamily: "var(--font-body)",
    fontVariantNumeric: "tabular-nums",
    fontSize: "0.96rem",
    letterSpacing: "0.04em",
    color: colorVar || "var(--text-strong)",
    textShadow: "none",
    fontWeight: colorVar === "var(--live)" ? 600 : 400,
  }),

  /* Falloff cell */
  falloff: {
    fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
    fontSize: "0.86rem",
    color: "var(--text-secondary)",
  },

  /* Laps count */
  laps: {
    fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
    fontSize: "0.92rem",
    color: "var(--text-secondary)",
  },

  /* Fuel cell */
  fuel: {
    fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
    fontSize: "0.86rem",
    color: "var(--live)",
  },

} as const;

function SetupHoverPanel({ entry, teamId, trackWeekId }: { entry: RankingEntry; teamId?: string; trackWeekId?: string }) {
  const parsed = parseSetupName(entry.setupName);
  const compareTo = teamId
    ? `/teams/${teamId}/setups/compare?run_a=${entry.runId}${trackWeekId ? `&track_week_id=${trackWeekId}` : ""}`
    : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.8125rem", color: "var(--text-muted)", wordBreak: "break-all", lineHeight: 1.5 }}>
        {entry.setupName.replace(/\\/g, " / ")}
      </div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: "0.95rem", fontWeight: 700, color: "var(--text-strong)" }}>
        {parsed.name}
      </div>
      {parsed.version && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.8125rem", color: "var(--live)" }}>{parsed.version}</div>
      )}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.1rem" }}>
        <Link to={`/runs/${entry.runId}`} className="btn btn-primary" style={{ padding: "0.3rem 0.7rem", fontSize: "0.8125rem" }}>
          View setup
        </Link>
        {compareTo && (
          <Link to={compareTo} className="btn btn-ghost" style={{ padding: "0.3rem 0.7rem", fontSize: "0.8125rem" }}>
            Compare
          </Link>
        )}
      </div>
    </div>
  );
}

export default function ComparisonTable({ rankings, onSort }: Props) {
  const navigate = useNavigate();
  const { teamId, trackWeekId } = useParams<{ teamId?: string; trackWeekId?: string }>();
  const [sortField, setSortField] = useState<SortableField>("top10_avg");
  const [clickedRow, setClickedRow] = useState<string | null>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSort = (field: SortableField) => {
    setSortField(field);
    onSort?.(field);
  };

  const handleRowClick = (runId: string) => {
    /* Brief click-down animation before navigating */
    setClickedRow(runId);
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      navigate(`/runs/${runId}`);
      setClickedRow(null);
    }, 120);
  };

  if (rankings.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.racingStripe} />
        <div className="empty-state">
          <div className="empty-state-title">No ranked runs yet</div>
          <div className="empty-state-text">Once runs are uploaded for this week, the board fills in.</div>
        </div>
      </div>
    );
  }

  /* ── Compute best/worst per numeric column ── */
  const bestValues: Record<string, number> = {};
  const worstValues: Record<string, number> = {};
  for (const field of NUMERIC_FIELDS) {
    const vals = rankings
      .map((r) => r[field] as number | null | undefined)
      .filter((v): v is number => v != null);
    if (vals.length > 1) {
      bestValues[field] = Math.min(...vals);
      worstValues[field] = Math.max(...vals);
    }
  }

  function cellColor(field: string, value: number | null | undefined): string | undefined {
    if (value == null) return undefined;
    if (bestValues[field] !== undefined && value === bestValues[field]) return "var(--live)";
    if (worstValues[field] !== undefined && value === worstValues[field]) return "var(--flag-orange)";
    return undefined;
  }

  /* ── Active sort detection uses snake_case sortKey ── */
  function isSortActive(col: Column): boolean {
    if (!col.sortKey) return false;
    return sortField === col.sortKey;
  }

  return (
    <div style={styles.container}>
      {/* Racing stripe accent */}
      <div style={styles.racingStripe} />

      <div style={styles.scrollWrapper}>
        <table style={styles.table}>
          {/* Column widths */}
          <colgroup>
            <col style={{ width: "48px" }} />   {/* # */}
            <col style={{ width: "130px" }} />  {/* Driver */}
            <col style={{ width: "80px" }} />   {/* Setup */}
            <col style={{ width: "90px" }} />   {/* Best */}
            <col style={{ width: "90px" }} />   {/* Top 10 */}
            <col style={{ width: "90px" }} />   {/* Top 20 */}
            <col style={{ width: "90px" }} />   {/* Top 30 */}
            <col style={{ width: "90px" }} />   {/* Avg */}
            <col style={{ width: "100px" }} />  {/* Falloff */}
            <col style={{ width: "52px" }} />   {/* Laps */}
            <col style={{ width: "110px" }} />  {/* Weather */}
            <col style={{ width: "60px" }} />   {/* Fuel */}
          </colgroup>

          <thead style={styles.thead}>
            <tr>
              {COLUMNS.map((col) => {
                const isSortable = !!col.sortKey;
                const isActive = isSortActive(col);
                return (
                  <th
                    key={col.key}
                    className="cmp-th"
                    data-sortable={isSortable ? "1" : "0"}
                    style={styles.th(isActive, isSortable)}
                    onClick={col.sortKey ? () => handleSort(col.sortKey!) : undefined}
                  >
                    {col.label}
                    {isSortable && isActive && (
                      <span style={styles.sortArrow(isActive)}>
                        {"\u25B2"}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {rankings.map((entry, idx) => {
              const isP1 = entry.rank === 1;
              const isEven = idx % 2 === 1;
              const isClicked = clickedRow === entry.runId;

              return (
                <tr
                  key={entry.runId}
                  className="cmp-row"
                  data-p1={isP1 ? "1" : "0"}
                  style={styles.row(isP1, isClicked)}
                  onClick={() => handleRowClick(entry.runId)}
                >
                  {/* Left accent border (hover) or P1 glow (always) */}
                  <td style={{ ...styles.td(isEven, isP1), position: "relative", textAlign: "center" }}>
                    {isP1 && <div style={styles.p1Glow} />}
                    {!isP1 && <div className="cmp-accent" style={styles.rowAccent(isP1)} />}
                    <span style={styles.rankBadge(entry.rank, rankings.length)}>
                      {isP1 ? (
                        <span style={styles.p1RankPill}>P1</span>
                      ) : (
                        `P${entry.rank}`
                      )}
                    </span>
                  </td>

                  {/* Driver */}
                  <td style={styles.td(isEven, isP1)}>
                    <span style={styles.driverName(isP1)}>
                      {entry.displayName || "Unknown"}
                    </span>
                  </td>

                  {/* Setup */}
                  <td style={styles.td(isEven, isP1)}>
                    <HoverPopover
                      width={320}
                      anchor={<span style={styles.setupTag}>{parseSetupName(entry.setupName).display}</span>}
                    >
                      <SetupHoverPanel entry={entry} teamId={teamId} trackWeekId={trackWeekId} />
                    </HoverPopover>
                  </td>

                  {/* Best Lap */}
                  <td style={{ ...styles.td(isEven, isP1) }}>
                    <span style={styles.lapTime(cellColor("bestLapTime", entry.bestLapTime))}>
                      {formatLapTime(entry.bestLapTime)}
                    </span>
                  </td>

                  {/* Top 10 Avg */}
                  <td style={{ ...styles.td(isEven, isP1) }}>
                    <span style={styles.lapTime(cellColor("top10Avg", entry.top10Avg))}>
                      {formatLapTime(entry.top10Avg)}
                    </span>
                  </td>

                  {/* Top 20 Avg */}
                  <td style={{ ...styles.td(isEven, isP1) }}>
                    <span style={styles.lapTime(cellColor("top20Avg", entry.top20Avg))}>
                      {formatLapTime(entry.top20Avg)}
                    </span>
                  </td>

                  {/* Top 30 Avg */}
                  <td style={{ ...styles.td(isEven, isP1) }}>
                    <span style={styles.lapTime(cellColor("top30Avg", entry.top30Avg))}>
                      {formatLapTime(entry.top30Avg)}
                    </span>
                  </td>

                  {/* Overall Avg */}
                  <td style={{ ...styles.td(isEven, isP1) }}>
                    <span style={styles.lapTime(cellColor("avgLapTime", entry.avgLapTime))}>
                      {formatLapTime(entry.avgLapTime)}
                    </span>
                  </td>

                  {/* Falloff */}
                  <td style={styles.td(isEven, isP1)}>
                    <span style={styles.falloff}>
                      {formatFalloff(entry.falloffMsPerLap)}
                    </span>
                  </td>

                  {/* Laps */}
                  <td style={styles.td(isEven, isP1)}>
                    <span style={styles.laps}>
                      {entry.greenLaps || "\u2014"}
                    </span>
                  </td>

                  {/* Weather */}
                  <td style={styles.td(isEven, isP1)}>
                    {entry.weather ? (
                      <HoverPopover width={250} anchor={<WeatherBadge weather={entry.weather} compact />}>
                        <WeatherBadge weather={entry.weather} />
                      </HoverPopover>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>{"\u2014"}</span>
                    )}
                  </td>

                  {/* Fuel */}
                  <td style={styles.td(isEven, isP1)}>
                    <span style={styles.fuel}>
                      {entry.fuelPerLap != null
                        ? `${fuelValueForDisplay(entry.fuelPerLap, entry.weather?.units)?.toFixed(2) ?? "\u2014"}`
                        : "\u2014"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
